import { FormEvent, useEffect, useMemo, useState } from "react";
import { getDashboard, getSession, sendChat, sendIntentStreaming, type IntentEvent } from "./api";
import CapacityObservability from "./CapacityObservability";
import type { ChatReceipt, ChatReply, DashboardSummary, Session } from "./types";

type View = "chat" | "dashboard" | "capacity";

const capacityOfflineEnabled =
  import.meta.env.VITE_CAPACITY_OBSERVABILITY_OFFLINE === "true";

const number = new Intl.NumberFormat("vi-VN");
const money = new Intl.NumberFormat("vi-VN", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2
});

function pct(value: number, limit: number): number {
  if (limit <= 0) return 0;
  return Math.min(100, Math.max(0, (value / limit) * 100));
}

function formatTime(value: string): string {
  if (!value) return "Chưa có";
  return new Intl.DateTimeFormat("vi-VN", {
    timeZone: "Asia/Bangkok",
    dateStyle: "short",
    timeStyle: "medium"
  }).format(new Date(value));
}

function StatusPill({ value }: { value: string }) {
  const tone =
    value.includes("ACTIVE") || value === "HEALTHY" || value === "COMPLETED"
      ? "good"
      : value.includes("HOLD") ||
          value.includes("BLOCKED") ||
          value === "DEGRADED" ||
          value === "STALE"
        ? "warn"
        : value.includes("FAIL") || value === "UNAVAILABLE"
          ? "bad"
          : "neutral";
  return <span className={`pill ${tone}`}>{value.replaceAll("_", " ")}</span>;
}

function MetricBar({
  label,
  value,
  limit,
  detail
}: {
  label: string;
  value: number;
  limit: number;
  detail: string;
}) {
  const progress = pct(value, limit);
  return (
    <div className="metric-bar">
      <div className="metric-row">
        <span>{label}</span>
        <strong>{detail}</strong>
      </div>
      <div className="bar-track" aria-label={`${label}: ${progress.toFixed(1)}%`}>
        <span
          className={progress >= 90 ? "danger" : progress >= 75 ? "warning" : ""}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

function Dashboard({
  summary,
  freshness,
  error
}: {
  summary: DashboardSummary | null;
  freshness: string;
  error: string | null;
}) {
  if (!summary) {
    return (
      <section className="empty-state">
        <span className="pulse-dot" />
        <h2>Dashboard đang chờ projection</h2>
        <p>{error ?? "Chưa nhận được snapshot đã ký từ Synergy Core."}</p>
      </section>
    );
  }

  const { costs } = summary;
  return (
    <div className="dashboard-grid">
      <section className="panel cost-panel">
        <div className="panel-title">
          <div>
            <span className="eyebrow">Ngân sách AI</span>
            <h2>Chi phí & quota</h2>
          </div>
          <StatusPill value={costs.d1_mode} />
        </div>
        <div className="cost-kpis">
          <article>
            <span>Thực trả</span>
            <strong>{money.format(costs.actual_billed_usd)}</strong>
            <small>Không gồm quota miễn phí</small>
          </article>
          <article>
            <span>Giá trị ước tính</span>
            <strong>{money.format(costs.estimated_equivalent_usd)}</strong>
            <small>Dùng để tối ưu model</small>
          </article>
          <article>
            <span>Incident đang mở</span>
            <strong>{summary.incidents_active}</strong>
            <small>Stability: {summary.stability_window}</small>
          </article>
        </div>
        <MetricBar
          label="Workers AI neurons"
          value={costs.workers_ai_neurons_used}
          limit={costs.workers_ai_neurons_limit}
          detail={`${number.format(costs.workers_ai_neurons_used)} / ${number.format(
            costs.workers_ai_neurons_limit
          )}`}
        />
        <MetricBar
          label="DeepSeek rolling 30 ngày"
          value={costs.deepseek_rolling_spend_usd}
          limit={costs.deepseek_rolling_cap_usd}
          detail={`${money.format(costs.deepseek_rolling_spend_usd)} / ${money.format(
            costs.deepseek_rolling_cap_usd
          )}`}
        />
        <MetricBar
          label="D1 writes hôm nay"
          value={costs.d1_writes_used}
          limit={costs.d1_writes_limit}
          detail={`${number.format(costs.d1_writes_used)} / ${number.format(
            costs.d1_writes_limit
          )}`}
        />
      </section>

      <section className="panel">
        <div className="panel-title">
          <div>
            <span className="eyebrow">Routing</span>
            <h2>Model đang hoạt động</h2>
          </div>
          <span className="freshness">{freshness}</span>
        </div>
        <div className="model-list">
          {summary.models.map((model) => (
            <article className="model-row" key={model.id}>
              <div className="model-avatar">{model.provider.slice(0, 2).toUpperCase()}</div>
              <div>
                <strong>{model.model}</strong>
                <span>
                  {model.provider} · {model.role}
                </span>
              </div>
              <div className="model-state">
                <StatusPill value={model.status} />
                <small>{model.active_requests} request đang chạy</small>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="panel tasks-panel">
        <div className="panel-title">
          <div>
            <span className="eyebrow">Execution graph</span>
            <h2>Tiến độ công việc</h2>
          </div>
          <button className="ghost-button" disabled title="Override chỉ mở sau gate riêng">
            Admin Override
          </button>
        </div>
        <div className="task-list">
          {summary.tasks.map((task) => (
            <article className="task-row" key={task.id}>
              <div className="task-head">
                <div>
                  <strong>{task.title}</strong>
                  <span>
                    {task.id} · {task.phase}
                  </span>
                </div>
                <StatusPill value={task.status} />
              </div>
              <div className="task-progress">
                <span style={{ width: `${Math.min(100, Math.max(0, task.progress))}%` }} />
              </div>
              <small>
                {task.progress}% · SHA {task.exact_sha.slice(0, 10)}
              </small>
            </article>
          ))}
        </div>
      </section>

      <section className="panel">
        <div className="panel-title">
          <div>
            <span className="eyebrow">Infrastructure</span>
            <h2>Sức khỏe hệ thống</h2>
          </div>
        </div>
        <div className="service-grid">
          {summary.services.map((service) => (
            <article key={service.id}>
              <span className={`health-dot ${service.status.toLowerCase()}`} />
              <div>
                <strong>{service.label}</strong>
                <small>
                  {service.latency_ms === null ? "Không có latency" : `${service.latency_ms} ms`}
                  {" · "}
                  {formatTime(service.sampled_at)}
                </small>
              </div>
              <StatusPill value={service.status} />
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

function ReceiptPanel({ receipt }: { receipt: ChatReceipt }) {
  const verdict = receipt.verdict;
  const verdictTone =
    verdict === "PASS" ? "good" : verdict === "HOLD" ? "warn" : "bad";
  return (
    <div className="receipt-panel" data-testid="receipt-panel">
      <header>
        <span className={`pill ${verdictTone}`} data-testid="receipt-verdict">
          {verdict}
        </span>
        <div>
          <strong>STAGING SYNTHETIC RECEIPT</strong>
          <small>NON-PRODUCTION · {receipt.scope}</small>
        </div>
      </header>
      <dl>
        <div>
          <dt>task_id</dt>
          <dd className="mono" data-testid="receipt-task-id">
            {receipt.task_id}
          </dd>
        </div>
        <div>
          <dt>request_id</dt>
          <dd className="mono">{receipt.request_id}</dd>
        </div>
        <div>
          <dt>passing_property</dt>
          <dd className="mono">{receipt.passing_property}</dd>
        </div>
        <div>
          <dt>state_evidence_digest (sha256)</dt>
          <dd className="mono" data-testid="receipt-digest">
            {receipt.state_evidence_digest}
          </dd>
        </div>
        <div>
          <dt>generated_at</dt>
          <dd className="mono">{receipt.generated_at}</dd>
        </div>
        <div>
          <dt>source</dt>
          <dd className="mono">{receipt.source}</dd>
        </div>
      </dl>
      <p className="receipt-disclaimer">
        This is a synthetic receipt produced by the staging edge in
        SYNTHETIC_MODE. It is not a real audit receipt and must not be
        presented to a real customer as production evidence. The real
        audit ledger lives in Synergy Core and is the only authoritative
        source for production receipts (V3 scope, not V2).
      </p>
    </div>
  );
}

type StreamStep = {
  type: string;
  at: string;
  payload?: Record<string, unknown>;
};

function StreamTimeline({ steps }: { steps: StreamStep[] }) {
  if (steps.length === 0) return null;
  return (
    <ol className="stream-timeline" data-testid="stream-timeline">
      {steps.map((s, i) => (
        <li key={i} className="stream-step" data-type={s.type}>
          <span className="stream-step-type">{s.type}</span>
          <span className="stream-step-at">{new Date(s.at).toLocaleTimeString("vi-VN")}</span>
          {s.payload && Object.keys(s.payload).length > 0 && (
            <span className="stream-step-payload">
              {Object.entries(s.payload).slice(0, 2).map(([k, v]) => `${k}=${String(v).slice(0, 24)}`).join(" · ")}
            </span>
          )}
        </li>
      ))}
    </ol>
  );
}

function Chat() {
  const [message, setMessage] = useState("");
  const [reply, setReply] = useState<ChatReply | null>(null);
  const [steps, setSteps] = useState<StreamStep[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [streaming, setStreaming] = useState(false);

  async function submitStreaming(text: string, signal: AbortSignal) {
    setSteps([]);
    setStreaming(true);
    const { events } = await sendIntentStreaming(
      text,
      (e) => setSteps((prev) => [...prev, { type: e.type, at: e.at, payload: e.payload }]),
      signal
    );
    setStreaming(false);
    // Build a synthetic ChatReply from the COMPLETED event
    const completed = events.find(e => e.type === "COMPLETED");
    const stepCompleted = events.find(e => e.type === "STEP_COMPLETED");
    const verdict = (stepCompleted?.payload?.verdict ?? completed?.payload?.verdict ?? "PASS") as "PASS" | "HOLD" | "UNKNOWN";
    setReply({
      request_id: events[0]?.trace_id ?? "trace-pending",
      provider: "synergy-router",
      model: "ucp-lane-1",
      fallback_active: false,
      message: verdict === "PASS"
        ? "Đã nhận yêu cầu và chạy luồng UCP. Trong lane 1 (foundation + observe-only), đây là synthetic receipt."
        : verdict === "HOLD"
          ? "Yêu cầu rơi vào fail-closed: cần người duyệt trước khi tiếp tục."
          : "Yêu cầu có dữ liệu không đầy đủ. Vui lòng bổ sung evidence.",
      receipt: {
        task_id: String(stepCompleted?.payload?.task_id ?? "STG-CHAT-pending"),
        request_id: events[0]?.trace_id ?? "trace-pending",
        verdict,
        scope: "STAGING_SYNTHETIC_NON_PRODUCTION",
        passing_property: "synthetic:chat-reply",
        state_evidence_digest: "pending",
        generated_at: completed?.at ?? new Date().toISOString(),
        source: "synthetic:chat-synthetic-reply"
      }
    });
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const text = message.trim();
    if (!text || busy) return;
    const controller = new AbortController();
    setBusy(true);
    setError(null);
    try {
      // Lane 1 streaming: try SSE first, fall back to legacy /v1/chat if EventSource is missing
      if (typeof EventSource !== "undefined") {
        await submitStreaming(text, controller.signal);
      } else {
        const result = await sendChat(text, controller.signal);
        setReply(result);
      }
      setMessage("");
    } catch (reason) {
      const err = reason instanceof Error ? reason.message : "CHAT_REQUEST_FAILED";
      // Fallback to legacy /v1/chat if streaming fails
      if (err.includes("EVENTSOURCE_UNAVAILABLE") || err.includes("401") || err.includes("Failed to fetch")) {
        try {
          const result = await sendChat(text, controller.signal);
          setReply(result);
          setMessage("");
        } catch (fallbackErr) {
          setError(fallbackErr instanceof Error ? fallbackErr.message : err);
        }
      } else {
        setError(err);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="chat-shell">
      <section className="chat-hero">
        <span className="eyebrow">Controlled intelligence</span>
        <h1>Hôm nay chúng ta cùng giải quyết vấn đề gì?</h1>
        <p>
          Synergy phân loại yêu cầu, kiểm tra quyền và ngân sách trước khi chọn model.
          Những tác vụ rủi ro cao luôn dừng để con người quyết định.
        </p>
      </section>

      {reply && (
        <article className="assistant-card" data-testid="assistant-card">
          <header>
            <span className="brand-orb small">S</span>
            <div>
              <strong>SYNERGY</strong>
              <small>UCP lane 1 · streaming · {reply.receipt?.scope}</small>
            </div>
          </header>
          <p>{reply.message}</p>
          {steps.length > 0 && <StreamTimeline steps={steps} />}
          {reply.receipt && <ReceiptPanel receipt={reply.receipt} />}
        </article>
      )}

      <form className="composer" onSubmit={submit}>
        <textarea
          aria-label="Nội dung gửi Synergy"
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          placeholder="Mô tả mục tiêu, ràng buộc và kết quả bạn muốn..."
          maxLength={8000}
          rows={4}
        />
        <div className="composer-footer">
          <span>{message.length.toLocaleString("vi-VN")} / 8.000</span>
          <button type="submit" disabled={!message.trim() || busy}>
            {busy ? (streaming ? "Đang stream..." : "Đang định tuyến...") : "Gửi tới Synergy"}
          </button>
        </div>
      </form>
      {error && <p className="error-banner">{error}</p>}

      <div className="guardrail-row">
        <span>Không public publish</span>
        <span>Không lưu raw prompt tại D1</span>
        <span>Human giữ quyền cuối cùng</span>
      </div>
    </div>
  );
}

export default function App() {
  const [view, setView] = useState<View>("chat");
  const [session, setSession] = useState<Session | null>(null);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [freshness, setFreshness] = useState("UNKNOWN");
  const [dashboardError, setDashboardError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    getSession(controller.signal)
      .then((response) => setSession(response.data))
      .catch(() => setSession(null));
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (view !== "dashboard") return;
    let active = true;
    const controller = new AbortController();

    const refresh = async () => {
      try {
        const response = await getDashboard(controller.signal);
        if (!active) return;
        setSummary(response.data);
        setFreshness(response.freshness);
        setDashboardError(null);
      } catch (reason) {
        if (!active) return;
        setDashboardError(reason instanceof Error ? reason.message : "DASHBOARD_UNAVAILABLE");
      }
    };

    void refresh();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void refresh();
    }, 15_000);
    return () => {
      active = false;
      controller.abort();
      window.clearInterval(timer);
    };
  }, [view]);

  const canViewDashboard = useMemo(() => session?.role === "OPERATOR", [session]);
  const canViewCapacity = canViewDashboard && capacityOfflineEnabled;

  return (
    <div className="app-shell">
      <header className="topbar">
        <button className="brand" onClick={() => setView("chat")} aria-label="Mở Synergy Chat">
          <span className="brand-orb">S</span>
          <span>
            <strong>AI SYNERGY</strong>
            <small>The system that runs itself</small>
          </span>
        </button>
        <nav aria-label="Điều hướng chính">
          <button className={view === "chat" ? "active" : ""} onClick={() => setView("chat")}>
            Chat
          </button>
          {canViewDashboard && (
            <button
              className={view === "dashboard" ? "active" : ""}
              onClick={() => setView("dashboard")}
            >
              Dashboard
            </button>
          )}
          {canViewCapacity && (
            <button
              className={view === "capacity" ? "active" : ""}
              onClick={() => setView("capacity")}
            >
              Capacity Lab
            </button>
          )}
        </nav>
        <div className="identity">
          <span className="live-dot" />
          <span>
            <strong>{session?.display_name ?? "Đang xác thực"}</strong>
            <small>{session?.role ?? "Cloudflare Access"}</small>
          </span>
        </div>
      </header>

      <main>
        {view === "chat" ? (
          <Chat />
        ) : view === "capacity" && canViewCapacity ? (
          <CapacityObservability />
        ) : canViewDashboard ? (
          <Dashboard summary={summary} freshness={freshness} error={dashboardError} />
        ) : (
          <section className="empty-state">
            <h2>OPERATOR_ONLY</h2>
            <p>Cloudflare Access identity hiện tại không có quyền xem Dashboard.</p>
          </section>
        )}
      </main>
      <footer>
        <span>synergy.ai.vn</span>
        <span>Non-production pilot · Authority false on read models</span>
      </footer>
    </div>
  );
}
